import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatIST } from '@/lib/utils';

// the items are on the order screen already, and here they read as [object Object]
const HIDDEN_RAW_FIELDS = new Set(['updated_at', 'line_items']);

export default function AuditTimeline({ auditLogs }) {
  if (!auditLogs?.length) return null;

  return (
    <Card className="border-slate-100 shadow-card rounded-xl">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-sm font-semibold text-slate-600 uppercase tracking-wider">Order Timeline</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="relative max-h-96 overflow-auto pr-2">
          {/* Vertical line */}
          <div className="absolute left-[11px] top-3 bottom-3 w-0.5 bg-slate-200" />

          <div className="space-y-0">
            {auditLogs.map((log, idx) => {
              const actionColor = log.action === 'create' ? 'bg-green-500' :
                log.action === 'design_complete' || log.action === 'design_complete_item' ? 'bg-purple-500' :
                log.action === 'complete_stage' ? 'bg-blue-500' :
                log.action === 'record_payment' ? 'bg-emerald-500' :
                log.action === 'assign_designer' || log.action === 'reassign_designer' ? 'bg-amber-500' :
                'bg-slate-400';

              const actionLabel = log.action === 'create' ? 'Order Created' :
                log.action === 'update' ? 'Updated' :
                log.action === 'design_complete' ? 'Design Completed' :
                log.action === 'design_complete_item' ? 'Item Design Done' :
                log.action === 'complete_stage' ? 'Stage Completed' :
                log.action === 'record_payment' ? 'Payment Recorded' :
                log.action === 'assign_designer' ? 'Designer Assigned' :
                log.action === 'reassign_designer' ? 'Designer Reassigned' :
                log.action?.replace(/_/g, ' ');

              return (
                <div key={log.id} className="relative flex gap-4 pb-6 last:pb-0" data-testid={`timeline-event-${idx}`}>
                  {/* Dot */}
                  <div className="relative z-10 flex-shrink-0">
                    <div className={`h-[22px] w-[22px] rounded-full border-2 border-white shadow-sm flex items-center justify-center ${actionColor}`}>
                      <div className="h-2 w-2 rounded-full bg-white" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 pt-0.5">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-xs font-semibold text-slate-800">{actionLabel}</p>
                      <time className="text-[10px] text-slate-400 flex-shrink-0">
                        {formatIST(log.timestamp, { compact: true })}
                      </time>
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5">
                      by <span className="font-medium">{log.user_name}</span>
                    </p>
                    {log.details && (
                      <p className="text-[11px] text-slate-500 mt-1 bg-slate-50 rounded px-2 py-1">
                        {typeof log.details === 'string' ? log.details : (
                          Object.entries(log.details).filter(([k]) => k !== 'updated_at').map(([k, v]) => (
                            <span key={k} className="mr-2">{k.replace(/_/g, ' ')}: <span className="font-medium text-slate-700">{String(v)}</span></span>
                          ))
                        )}
                      </p>
                    )}
                    {log.after && typeof log.after === 'object' && !log.details && (
                      <div className="text-[11px] text-slate-500 mt-1 bg-slate-50 rounded px-2 py-1">
                        {Object.entries(log.after).filter(([k]) => !HIDDEN_RAW_FIELDS.has(k)).map(([k, v]) => (
                          <span key={k} className="mr-2">{k.replace(/_/g, ' ')}: <span className="font-medium text-slate-700">{String(v)}</span></span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
